import {
  BreadcrumbActionsProvider,
  useBreadcrumbActions,
} from '@/hooks/useBreadcrumbActions';
import { useTranslation } from '@/hooks/useTranslation';
import { Breadcrumb, BreadcrumbItem } from '@/layouts/Breadcrumb';
import { Sidebar } from '@/layouts/Sidebar';
import { TopBar } from '@/layouts/TopBar';
import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

export interface LayoutContext {
  setSurveyTitle: (title: string | null) => void;
  setProjectTitle: (title: string | null) => void;
}

const AuthenticatedLayoutInner: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [surveyTitle, setSurveyTitle] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState<string | null>(null);
  const breadcrumbActions = useBreadcrumbActions();

  // Placeholder breadcrumbs based on current path
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    if (pathSegments[0] === 'projects') {
      if (pathSegments[1] === 'new') {
        return [
          { label: t('layout.breadcrumb.projects'), path: '/projects' },
          { label: 'New Project' },
        ];
      }
      if (pathSegments[1] === 'edit') {
        return [
          { label: t('layout.breadcrumb.projects'), path: '/projects' },
          {
            label: `${t('layout.breadcrumb.editProject')}${projectTitle ? `: ${projectTitle}` : ''}`,
          },
        ];
      }
      // Viewing a project (not editing)
      if (
        pathSegments[1] &&
        pathSegments[1] !== 'new' &&
        pathSegments[1] !== 'edit'
      ) {
        return [
          { label: t('layout.breadcrumb.projects'), path: '/projects' },
          { label: projectTitle || 'Project' },
        ];
      }
      return [{ label: t('layout.breadcrumb.projects') }];
    }

    if (pathSegments[0] === 'surveys') {
      if (pathSegments[1] === 'new') {
        return [
          { label: t('layout.breadcrumb.surveys'), path: '/surveys' },
          { label: t('layout.breadcrumb.newSurvey') },
        ];
      }
      if (pathSegments[1] === 'edit') {
        return [
          { label: t('layout.breadcrumb.surveys'), path: '/surveys' },
          {
            label: `${t('layout.breadcrumb.editSurvey')}${surveyTitle ? `: ${surveyTitle}` : ''}`,
          },
        ];
      }
      if (pathSegments[2] === 'report') {
        return [
          { label: t('layout.breadcrumb.surveys'), path: '/surveys' },
          { label: t('layout.breadcrumb.reports') },
        ];
      }
      return [{ label: t('layout.breadcrumb.surveys') }];
    }

    if (pathSegments[0] === 'collections') {
      if (pathSegments[2] === 'report') {
        return [
          { label: t('layout.breadcrumb.surveys'), path: '/surveys' },
          { label: t('layout.breadcrumb.reports') },
        ];
      }
      return [{ label: t('layout.breadcrumb.surveys'), path: '/surveys' }];
    }

    if (pathSegments[0] === 'prompts') {
      return [{ label: t('layout.breadcrumb.prompts') }];
    }

    // Default fallback
    return [];
  };

  return (
    <div className='flex h-screen overflow-auto'>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className='flex flex-col flex-1 overflow-hidden'>
        <TopBar onMenuClick={() => setIsSidebarOpen(true)} />
        <Breadcrumb items={getBreadcrumbs()} rightActions={breadcrumbActions} />
        <main className='flex-1 overflow-y-auto p-4'>
          <Outlet context={{ setSurveyTitle, setProjectTitle }} />
        </main>
      </div>
    </div>
  );
};

export const AuthenticatedLayout: React.FC = () => {
  return (
    <BreadcrumbActionsProvider>
      <AuthenticatedLayoutInner />
    </BreadcrumbActionsProvider>
  );
};
